import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { History } from 'lucide-react'
import useAppStore from '../../../store/useAppStore'
import useAudioManager from '../../../hooks/useAudioManager'
import { hapticImpact, hapticNotify, hapticSelectionChanged } from '../../../utils/haptic'
import crashSocket from '../../crash/services/crashSocket'
import SlotGame from '../kit/SlotGame.jsx'
import userApi from '../../../services/userApi'
import audioService from '../../../services/audioService'
import Loader from '../../../components/Loader'
import '../slot-page.css'

/**
 * Экран игры «Слоты» — готовый пакет src/features/slot/kit (документация пакета:
 * docs/slots_kit). Вся игра и весь протокол slot:* внутри пакета, здесь только
 * подключение: общий сокет игрового сервера, баланс шапки, звук и колбэки.
 *
 * Право играть решает сервер: игроку без пополнений slot:open ответит
 * deposit_required (пакет покажет плашку и позовёт onDepositRequired), гость
 * получит unauthorized (открываем вход). Отдельного гейтинга по slot_enabled
 * на роуте нет — ключ нужен только точкам входа в раздел «Игры» (сейчас их
 * нет, вход по прямой ссылке /slot).
 */
// Вибрация пакета через общие хелперы Telegram HapticFeedback (вне Telegram — тишина)
const SLOT_HAPTIC = { impact: hapticImpact, notify: hapticNotify, selection: hapticSelectionChanged }

export default function Slot() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { playSound } = useAudioManager({ select: 'select' })
  const isGuest = useAppStore((state) => state.isGuest)
  const sound = useAppStore((state) => state.user?.sound !== false)
  const [socket, setSocket] = useState(null)
  const pageRef = useRef(null)

  // Таблица выплат пакета (.slt-modal) — fixed внутри изолированного .slt, поэтому
  // навигация приложения рисуется поверх неё. Пока таблица открыта, вешаем на
  // страницу класс, который поднимает весь слот над навигацией (slot-page.css).
  // Без :has() — его нет в старых Android WebView.
  useEffect(() => {
    const modal = pageRef.current?.querySelector('.slt-modal')
    if (!modal) return undefined
    const sync = () => pageRef.current?.classList.toggle('slot-modal-open', !modal.classList.contains('slt-hidden'))
    const observer = new MutationObserver(sync)
    observer.observe(modal, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [socket])

  // Подписка на слот через общий сокет: пакет сам шлёт subscribe/unsubscribe
  // и повторяет подписку на каждый connect, мы только держим соединение живым
  // всё время, пока открыт экран (refcount в crashSocket).
  useEffect(() => {
    crashSocket.subscribe('slot')
    setSocket(crashSocket.getSocket())
    // Пауза даёт slot:close из destroy() долететь до сервера до разрыва
    // соединения: сессия закрывается, резерв возвращается на баланс.
    return () => { setTimeout(() => crashSocket.unsubscribe('slot'), 300) }
  }, [])

  // После возврата с экрана оплаты перезапрашиваем /api/me/: у новичка
  // slot_enabled появляется сразу после первого пополнения (гайд, §2).
  useEffect(() => {
    const { isGuest, isAuthorized } = useAppStore.getState()
    if (isGuest || !isAuthorized) return undefined
    let active = true
    useAppStore.getState().loadProfile().then((response) => {
      const me = response?.me
      if (active && me) useAppStore.getState().setUser({ ...useAppStore.getState().user, ...me })
    }).catch(() => { /* локальный кеш профиля остаётся, помеха не критична */ })
    return () => { active = false }
  }, [])

  // Тумблер звука в шапке слота — тот же эндпоинт, что в настройках
  const handleSoundChange = async (enabled) => {
    try {
      const response = await userApi.updateSound(enabled)
      if (response?.sound?.result) {
        audioService.setMuted(!response.sound.enabled)
        useAppStore.getState().setUser({ ...useAppStore.getState().user, sound: response.sound.enabled })
      }
    } catch (error) {
      // Молча: при сбое остаётся прежнее значение, игрок может нажать снова
    }
  }

  // Пока открыта сессия, часть денег лежит в резерве и не видна в user:balance,
  // поэтому баланс шапки на экране слота показываем из onBalanceChange (shown =
  // основной + резерв минус ставка в полёте и невыданный выигрыш, гайд, §6).
  const handleBalanceChange = (balance) => {
    if (balance.shown != null) useAppStore.getState().setBalance(balance.shown)
  }

  return (
    <div ref={pageRef} className="slot-page min-h-[100dvh]">
      {socket
        ? (
          <SlotGame
            socket={socket}
            sound={sound}
            haptic={SLOT_HAPTIC}
            onBalanceChange={handleBalanceChange}
            onSoundChange={handleSoundChange}
            onDepositRequired={() => navigate('/payment')}
            onLoginRequired={() => useAppStore.getState().openLogin()}
          />
          )
        : <Loader message="" />}
      {/* Вход в личную историю спинов: в пакете своей кнопки нет (гайд, «История спинов») */}
      {socket && !isGuest && (
        <div className="mx-auto max-w-[820px] px-3 pb-6">
          <button type="button" onClick={() => { hapticImpact('light'); playSound('select'); navigate('/slot/history') }} className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-surface text-text-secondary transition-colors hover:border-accent-primary/40 hover:text-accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-primary"><History size={17} aria-hidden="true" />{t('slot.history.title')}</button>
        </div>
      )}
    </div>
  )
}
